import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Category } from 'anchor-shared/lib/model/enum/vessel-category-types';
import { CategoryType2LabelMapping } from 'anchor-shared/lib/service/pure-functions/vessel-helper/CategoryType2LabelMapping';
import * as moment from 'moment';
import { Moment } from 'moment';
import { DialogsService } from '../../../../core/services/dialog.service';
import { Subscription } from 'rxjs';
import { unsubscribeFromAll } from '../../../../core/pure-functions/rx-helper';
import {
  buildRouteEnding,
  buildRouteEndingForVesselAvailability,
} from '../../../../core/pure-functions/navigation/buyflow-vessel-route-builder/buyflow-vessel-route-builder';
import { BreakpointObserver, BreakpointState } from '@angular/cdk/layout';
import { MatBottomSheet } from '@angular/material/bottom-sheet';
import { VesselTotalPriceDialogComponent } from './vessel-total-price-dialog/vessel-total-price-dialog.component';
import { EnvironmentalMediaService } from '../../../../core/services/environmental-media.service';
import { Vessel } from 'anchor-shared/lib/model/entity/vessel';
import { VesselAvailability } from 'anchor-shared/lib/model/entity/vesselAvailability';
import { CharterType } from 'anchor-shared/lib/model/enum/charter-type';

@Component({
  selector: 'app-carousel-vessel-card',
  templateUrl: './carousel-vessel-card.component.html',
  styleUrls: ['./carousel-vessel-card.component.scss'],
})
export class CarouselVesselCardComponent implements OnInit, OnDestroy {
  @Input()
  vessel: VesselAvailability;

  @Input()
  reviewCount: number;

  @Input()
  waterBodyDisplayName: string;

  @Input()
  filterDate: Moment;

  @Input()
  stackContent: boolean;

  @Input()
  charterType: CharterType;

  public CategoryType2LabelMapping = CategoryType2LabelMapping;

  public CategoryTypes = Object.values(Category);

  componentClass: CarouselVesselCardComponentClassType;
  decimalPercentage: number;
  halfDayProductExists: boolean;
  fullTotal = 999999;
  subs: Subscription[] = [];
  isMobile: boolean;
  dateIsValid: boolean;
  CharterType = CharterType;

  constructor(
    private dialogService: DialogsService,
    private breakpointObserver: BreakpointObserver,
    private matBottomSheet: MatBottomSheet,
    public environmentalMediaService: EnvironmentalMediaService,
  ) {
    const sub = this.breakpointObserver
      .observe(['(min-width: 960px)'])
      .subscribe((state: BreakpointState) => {
        this.isMobile = !state.matches;
      });
    this.subs.push(sub);
  }

  ngOnInit() {
    if (this.vessel.displayTitle && this.vessel.displayTitle.length > 40) {
      this.vessel.displayTitle =
        this.vessel.displayTitle.substring(0, 40) + '...';
    }

    if (
      this.waterBodyDisplayName &&
      this.waterBodyDisplayName.indexOf(',') > -1
    ) {
      this.waterBodyDisplayName = this.waterBodyDisplayName.substring(
        0,
        this.waterBodyDisplayName.indexOf(','),
      );
    }
    this.componentClass = this.getComponentClass();
    this.dateIsValid = this.filterDate && this.filterDate.isValid();
    this.calculateTotal();
  }

  getComponentClass(): CarouselVesselCardComponentClassType {
    if (this.stackContent) {
      return 'vessel-card-stacked-gc';
    }
    return 'vessel-card-gc';
  }

  checkAvailability(vesselAvailability: VesselAvailability): void {
    window.open(
      'vessels/yacht-and-boat-rentals/' +
        buildRouteEndingForVesselAvailability(vesselAvailability) +
        '?charterType=' +
        this.charterType,
    );
  }

  checkDurationAvailability(vessel: Vessel, number: number) {
    return vessel.activeBuyflowProducts.some(
      (product) => product.duration === number,
    );
  }

  checkPriceAdjustment(vessel: Vessel, number: number) {
    return vessel.activeBuyflowProducts.some(
      (product) =>
        product.duration === number && product.discount > 0 && !product.premium,
    );
  }

  calculateTotal(): void {
    this.fullTotal = 0;

    if (
      this.vessel.halfDayPrice &&
      this.vessel.halfDayLineItems &&
      this.vessel.halfDayLineItems.length
    ) {
      this.vessel.halfDayLineItems.forEach((item) => {
        if (item) {
          this.fullTotal += item.price;
          this.fullTotal += item.estimatedTax;
        }
      });
    } else if (
      this.vessel.fullDayPrice &&
      this.vessel.fullDayLineItems &&
      this.vessel.fullDayLineItems.length
    ) {
      this.vessel.fullDayLineItems.forEach((item) => {
        if (item) {
          this.fullTotal += item.price;
          this.fullTotal += item.estimatedTax;
        }
      });
    } else {
      this.fullTotal =
        this.vessel.halfDayOriginalTotalPrice <= 0
          ? this.vessel.fullDayOriginalTotalPrice
          : this.vessel.halfDayOriginalTotalPrice;
    }
  }

  viewPricingModal(): void {
    if (!this.isMobile) {
      const dialogSub = this.dialogService
        .vesselListPriceDialog(
          this.vessel.halfDayPrice,
          this.vessel.halfDayDiscountApplied,
          this.vessel.halfDayOriginalTotalPrice,
          this.vessel.fullDayPrice,
          this.vessel.fullDayDiscountApplied,
          this.vessel.fullDayOriginalTotalPrice,
          this.vessel.halfDayLineItems,
          this.vessel.fullDayLineItems,
          this.vessel.name,
          this.vessel.vesselId,
          this.dateIsValid,
        )
        .subscribe();
      this.subs.push(dialogSub);
    } else if (this.isMobile) {
      this.matBottomSheet.open(VesselTotalPriceDialogComponent, {
        data: {
          halfDayPrice: this.vessel.halfDayPrice,
          fullDayPrice: this.vessel.fullDayPrice,
          halfDayDiscount: this.vessel.halfDayDiscountApplied,
          halfDayOriginalTotalPrice: this.vessel.halfDayOriginalTotalPrice,
          fullDayDiscount: this.vessel.fullDayDiscountApplied,
          fullDayOriginalTotalPrice: this.vessel.fullDayOriginalTotalPrice,
          halfDayLineItems: this.vessel.halfDayLineItems,
          fullDayLineItems: this.vessel.fullDayLineItems,
          vesselName: this.vessel.name,
          vesselId: this.vessel.vesselId,
          isDateSelected: this.dateIsValid,
        },
      });
    }
  }

  ngOnDestroy(): void {
    unsubscribeFromAll(this.subs);
  }
}

export type CarouselVesselCardComponentClassType =
  | 'vessel-card-gc'
  | 'vessel-card-stacked-gc';
